import React from "react";
import { Tooltip } from "@mui/material";
import MDBox from "components/MDBox";
import { STANDARD_RANK, STANDARD_LABEL, portText } from "./constants";

/*
 * POL -> transshipments -> POD as a progress bar. Position comes from the
 * latest actual Filentech milestone, so it never runs ahead of the carrier.
 */
const progressOf = (currentStatus, legs) => {
    const rank = STANDARD_RANK[currentStatus];
    if (rank === undefined || rank <= STANDARD_RANK.GATE_IN) return 0;
    if (rank >= STANDARD_RANK.VESSEL_ARRIVED) return legs;
    if (rank === STANDARD_RANK.LOADED_ON_VESSEL) return 0.08;
    if (rank === STANDARD_RANK.VESSEL_DEPARTED) return Math.min(0.5, legs);
    if (rank === STANDARD_RANK.TRANSSHIPMENT_ARRIVAL) return Math.min(1, legs);
    if (rank === STANDARD_RANK.TRANSSHIPMENT_DEPARTURE) return Math.min(1.5, legs);
    return 0;
};

function JourneyBar({ tracking, compact = false }) {
    const stops = [
        { role: "POL", ...tracking.pol },
        ...(tracking.transshipmentPorts || []).map((p) => ({ role: "T/S", ...p })),
        { role: "POD", ...tracking.pod },
    ].filter((s) => portText(s));

    if (stops.length < 2) {
        return (
            <MDBox component="span" sx={{ fontSize: 12, color: "#94a3b8" }}>
                Route not reported yet
            </MDBox>
        );
    }

    const legs = stops.length - 1;
    const progress = progressOf(tracking.currentStatus, legs);
    const percent = (progress / legs) * 100;
    const done = tracking.currentStatus === "EMPTY_RETURNED" || tracking.trackingStatus === "COMPLETED";
    const accent = done ? "#16a34a" : "#1d4ed8";

    return (
        <Tooltip arrow title={tracking.currentStatus ? STANDARD_LABEL[tracking.currentStatus] : "No actual event yet"}>
            <MDBox sx={{ minWidth: compact ? 170 : 260, pt: "6px" }}>
                <MDBox sx={{ position: "relative", height: 14, mx: "6px" }}>
                    <MDBox sx={{ position: "absolute", top: 6, left: 0, right: 0, height: 2, borderRadius: 2, background: "#e2e8f0" }} />
                    <MDBox
                        sx={{
                            position: "absolute",
                            top: 6,
                            left: 0,
                            width: `${percent}%`,
                            height: 2,
                            borderRadius: 2,
                            background: accent,
                            transition: "width .3s",
                        }}
                    />
                    {stops.map((stop, i) => {
                        const reached = progress >= i;
                        return (
                            <MDBox
                                key={`${stop.role}-${i}`}
                                sx={{
                                    position: "absolute",
                                    top: 2,
                                    left: `calc(${(i / legs) * 100}% - 5px)`,
                                    width: 10,
                                    height: 10,
                                    borderRadius: "50%",
                                    background: reached ? accent : "#fff",
                                    border: `2px solid ${reached ? accent : "#cbd5e1"}`,
                                }}
                            />
                        );
                    })}
                    {!done && progress > 0 && progress < legs && (
                        <MDBox
                            sx={{
                                position: "absolute",
                                top: -1,
                                left: `calc(${percent}% - 8px)`,
                                width: 16,
                                height: 16,
                                borderRadius: "50%",
                                background: "#fff",
                                border: `2px solid ${accent}`,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 9,
                                color: accent,
                            }}
                        >
                            ▲
                        </MDBox>
                    )}
                </MDBox>
                <MDBox sx={{ position: "relative", height: 16, mt: "4px" }}>
                    {stops.map((stop, i) => (
                        <MDBox
                            key={`label-${i}`}
                            component="span"
                            sx={{
                                position: "absolute",
                                left: `${(i / legs) * 100}%`,
                                transform: i === 0 ? "none" : i === legs ? "translateX(-100%)" : "translateX(-50%)",
                                fontSize: 11,
                                fontWeight: 600,
                                color: progress >= i ? "#334155" : "#94a3b8",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {portText(stop)}
                        </MDBox>
                    ))}
                </MDBox>
            </MDBox>
        </Tooltip>
    );
}

export default JourneyBar;
